import { BRAND } from "constants/brand";
import "./menuExtraction.scss";
import { useMemo, useState } from "react";
import { Ring } from "@uiball/loaders";
import { useNavigate } from "react-router-dom";
import { extractMenu, commitMenu } from "services/menuExtraction";
import UploadView from "./UploadView";
import ReviewView from "./ReviewView";

/**
 * Single-page menu extraction:
 *   uploadView (vendor + drag-drop) → extract → reviewView (edit + approve) → commit
 *
 * No multi-screen navigation. No polling. Browser tab IS the staging area
 * between extract and commit.
 */
const MenuExtraction = ({ showMessage }) => {
    const [vendorId, setVendorId] = useState(null);
    const [images, setImages] = useState([]); // File[]
    const [extracted, setExtracted] = useState(null); // { vendorId, model, images: [...] }
    const [submitting, setSubmitting] = useState(false);
    const [committing, setCommitting] = useState(false);
    const navigate = useNavigate();

    // Build object URLs once per file so we don't allocate them every render.
    const imageThumbs = useMemo(
        () =>
            images.map((file) => ({
                file,
                url: URL.createObjectURL(file),
            })),
        [images],
    );

    const handleExtract = async () => {
        if (!vendorId || images.length === 0) return;

        const form = new FormData();
        form.append("vendorId", String(vendorId));
        images.forEach((img) => form.append("images", img));

        setSubmitting(true);
        try {
            const data = await extractMenu(form);
            // Add ui-only fields per extracted item: approved, edited fields, mappedCategoryId.
            const withUiState = {
                ...data,
                images: (data.images ?? []).map((img, imgIdx) => ({
                    ...img,
                    previewUrl: imageThumbs[imgIdx]?.url ?? null,
                    items: (img.items ?? []).map((it) => ({
                        ...it,
                        approved: !it.error,
                        mappedCategoryId: null,
                    })),
                })),
            };
            setExtracted(withUiState);
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                "تعذر استخراج القائمة، حاول مرة أخرى";
            showMessage?.("error", "خطأ في الاستخراج", msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCommit = async () => {
        const approved = extracted.images.flatMap((img) =>
            img.items
                .filter((i) => i.approved && i.title?.trim())
                .map((i) => ({
                    title: i.title,
                    description: i.description ?? "",
                    price: typeof i.price === "number" ? i.price : Number(i.price) || 0,
                    categoryName: i.suggested_category_name ?? null,
                    mappedCategoryId: i.mappedCategoryId ?? null,
                })),
        );

        if (approved.length === 0) {
            showMessage?.("warn", "لا توجد منتجات", "اعتمد منتجاً واحداً على الأقل قبل الحفظ");
            return;
        }

        setCommitting(true);
        try {
            const data = await commitMenu({ vendorId, items: approved });
            showMessage?.("success", "تم الحفظ", `تم إضافة ${data.committedCount} منتج`);
            navigate(`/products?vendor=${vendorId}`);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "فشل حفظ المنتجات";
            showMessage?.("error", "خطأ في الحفظ", msg);
        } finally {
            setCommitting(false);
        }
    };

    const handleStartOver = () => {
        // Revoke any object URLs we still hold.
        imageThumbs.forEach((t) => URL.revokeObjectURL(t.url));
        setExtracted(null);
        setImages([]);
    };

    const busy = submitting || committing;

    return (
        <div className="menu-extraction">
            {busy ? (
                <div className="loading-spinner center">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                    <p>
                        {submitting
                            ? "جارٍ استخراج العناصر بالذكاء الاصطناعي..."
                            : "جارٍ حفظ المنتجات..."}
                    </p>
                </div>
            ) : null}

            <h3>استخراج القوائم بالذكاء الاصطناعي</h3>

            {!extracted ? (
                <UploadView
                    vendorId={vendorId}
                    setVendorId={setVendorId}
                    images={images}
                    setImages={setImages}
                    imageThumbs={imageThumbs}
                    onExtract={handleExtract}
                    submitting={submitting}
                />
            ) : (
                <ReviewView
                    extracted={extracted}
                    setExtracted={setExtracted}
                    onCommit={handleCommit}
                    onStartOver={handleStartOver}
                    committing={committing}
                />
            )}
        </div>
    );
};

export default MenuExtraction;
